import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Spinner,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  VStack,
  Stack,
  Badge,
  HStack,
  Select,
  Input,
  Heading,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { RootState } from "../../store/store";
import { fetchAvailable } from "../../store/deliverySlice/deliverySlice";
import axios from "axios";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../configs/firebase";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { usePagination } from "../../store/hooks/usePagination";
import AppPagination from "../../components/Pagination/AppPagination";
import GlobalSearch from "../../components/Search/GlobalSearch";

/* ================= CONFIG ================= */
const API_BASE = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const cardsBorderRadii ="8px";

type DeliveryRow = {
  _id: string;
  userId?: string;
  ownerUserId?: string;
  senderUserId?: string;
  senderId?: string;
  requesterUserId?: string;
  buyerUserId?: string;
  createdBy?: string;
  status?: string;
  name?: string;
  phone?: string;
  driverId?: string | null;
  createdAt?: string;
  pickup?: { address?: string };
  dropoff?: { address?: string };
};

const cleanAccessValue = (value: unknown) => String(value || "").trim();

const normalizeAccessRole = (value: unknown) =>
  cleanAccessValue(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getInlineAccessRole = (user: any) =>
  user?.role ||
  user?.userRole ||
  user?.accountRole ||
  user?.adminRole ||
  user?.claims?.role ||
  user?.customClaims?.role ||
  "";

const isSuperAdminRole = (value: unknown) =>
  normalizeAccessRole(value) === "superadmin";

const extractDeliveryArray = (payload: any): DeliveryRow[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.deliveries)) return payload.deliveries;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data?.deliveries)) return payload.data.deliveries;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const getDeliveryAccountIds = (delivery: DeliveryRow) => {
  const values = [
    delivery.ownerUserId,
    delivery.senderUserId,
    delivery.senderId,
    delivery.requesterUserId,
    delivery.createdBy,
    delivery.userId,
    delivery.buyerUserId,
  ];

  return new Set(values.map(cleanAccessValue).filter(Boolean));
};

const isDeliveryOwnedByAccount = (
  delivery: DeliveryRow,
  userId: string,
  allowLegacyScopedRecord = false,
) => {
  const uid = cleanAccessValue(userId);
  if (!uid) return false;

  const accountIds = getDeliveryAccountIds(delivery);

  // fetchAvailable already scopes legacy records server-side. Keep those
  // records visible when old documents do not yet contain an owner field.
  if (accountIds.size === 0) return allowLegacyScopedRecord;

  return accountIds.has(uid);
};

export const DeliveryList = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const loggedInUserId = cleanAccessValue(user?.uid);
  const [resolvedAccessRole, setResolvedAccessRole] = useState(() =>
    getInlineAccessRole(user),
  );
  const [accessLoading, setAccessLoading] = useState(true);
  const [superAdminLoading, setSuperAdminLoading] = useState(false);
  const [superAdminDeliveries, setSuperAdminDeliveries] = useState<
    DeliveryRow[]
  >([]);

  const isSuperAdmin = isSuperAdminRole(resolvedAccessRole);

  const { deliveries, loading } = useSelector(
    (state: RootState) => state.deliveryRequests,
  );

  const reduxDeliveries = (deliveries ?? []) as DeliveryRow[];

  const safeDeliveries = useMemo(() => {
    const source = isSuperAdmin ? superAdminDeliveries : reduxDeliveries;

    if (isSuperAdmin) return source;

    return source.filter((delivery) =>
      isDeliveryOwnedByAccount(delivery, loggedInUserId, true),
    );
  }, [isSuperAdmin, loggedInUserId, reduxDeliveries, superAdminDeliveries]);

  const [drivers, setDrivers] = useState<any[]>([]);

  // ✅ operational controls
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [groupBy, setGroupBy] = useState<"none" | "status" | "driver">("none");

  /* ================= THEME (same pattern) ================= */
  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    deepSlate: "#2a2c3b",
    deepInk: "#1a1c2f",
    slate2: "#2e3143",
    softLight: "#eaebf0",
    violet: "#b05cff",
    indigo: "#6f7cff",
  };

  const gradients = theme.gradients || {
    pageBg: theme.background,
    shellBg: theme.surface,
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    secondaryButton: `linear-gradient(135deg, ${brand.indigo} 0%, ${brand.electricBlue} 100%)`,
    tabHikesSelected: `linear-gradient(135deg, rgba(39,183,255,0.14) 0%, rgba(73,209,255,0.08) 100%)`,
  };

  const pageBg = gradients.pageBg || theme.background;
  const shellBg = gradients.shellBg || theme.surface;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;

  const panelShadow = theme.shadowMd;
  const softShadow = theme.shadowSm;

  /* ================= LOAD ACCESS ================= */
  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setResolvedAccessRole("");
          setAccessLoading(false);
          setSuperAdminDeliveries([]);
        }
        return;
      }

      setAccessLoading(true);

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const profile = snapshot.exists() ? snapshot.data() : {};
        const role =
          profile?.role || profile?.userRole || getInlineAccessRole(user);

        if (!cancelled) setResolvedAccessRole(String(role || ""));
      } catch (accessError) {
        console.error("Failed to resolve Delivery Admin access:", accessError);

        if (!cancelled) {
          setResolvedAccessRole(String(getInlineAccessRole(user) || ""));
        }
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  /* ================= LOAD REGISTERED DRIVERS ================= */
  const loadDrivers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/v1/drivers`);
      const payload = response.data;
      const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.drivers)
            ? payload.drivers
            : Array.isArray(payload?.data?.drivers)
              ? payload.data.drivers
              : [];

      setDrivers(
        rows.map((driver: any) => ({
          ...driver,
          id: cleanAccessValue(
            driver?.id || driver?._id || driver?.userId || driver?.firebaseUid,
          ),
        })),
      );
    } catch (err) {
      console.error("Failed to load registered drivers", err);
      setDrivers([]);
    }
  };

  const loadDeliveries = async () => {
    if (!loggedInUserId) {
      setSuperAdminDeliveries([]);
      return;
    }

    if (isSuperAdmin) {
      try {
        setSuperAdminLoading(true);

        const response = await axios.get(`${API_BASE}/api/delivery`);
        setSuperAdminDeliveries(extractDeliveryArray(response.data));
      } catch (err) {
        console.error("Failed to load all deliveries", err);
        setSuperAdminDeliveries([]);
      } finally {
        setSuperAdminLoading(false);
      }

      return;
    }

    await dispatch(
      fetchAvailable({
        userId: loggedInUserId,
        role: "sender" as const,
      }),
    );
  };

  useEffect(() => {
    if (accessLoading || !loggedInUserId) return;

    void loadDeliveries();
    void loadDrivers();

    // The access role and UID are intentionally dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessLoading, dispatch, isSuperAdmin, loggedInUserId]);

  const refresh = async () => {
    await loadDeliveries();
  };

  const canManageDelivery = (delivery: DeliveryRow) =>
    isSuperAdmin || isDeliveryOwnedByAccount(delivery, loggedInUserId, true);

  const getDeliveryForAction = (deliveryId: string) =>
    safeDeliveries.find(
      (delivery) =>
        cleanAccessValue(delivery._id) === cleanAccessValue(deliveryId),
    );

  const ensureDeliveryAccess = (deliveryId: string) => {
    const delivery = getDeliveryForAction(deliveryId);

    if (!delivery || !canManageDelivery(delivery)) {
      console.error(
        "Access denied: delivery is not linked to the signed-in account.",
      );
      return false;
    }

    return true;
  };

  /* ================= DRIVER NAME RESOLVER ================= */
  const driverMap = useMemo(() => {
    const map: Record<string, string> = {};
    drivers.forEach((d) => {
      map[d.id] = d.name || "Unnamed Driver";
    });
    return map;
  }, [drivers]);

  const getDriverName = (driverId: string | null | undefined) => {
    if (!driverId) return "Unassigned";
    return driverMap[driverId] || "Unknown Driver";
  };

  /* ================= ACTIONS (UNCHANGED) ================= */
  const assignDriver = async (deliveryId: string, driverId: string) => {
    if (!ensureDeliveryAccess(deliveryId)) return;

    try {
      await axios.patch(`${API_BASE}/api/delivery/${deliveryId}/assign`, {
        driverId,
      });
      refresh();
    } catch (err) {
      console.error("Assign failed", err);
    }
  };

  const acceptDelivery = async (deliveryId: string) => {
    if (!ensureDeliveryAccess(deliveryId)) return;

    await axios.patch(`${API_BASE}/api/delivery/${deliveryId}/accept`, {
      driverId: loggedInUserId,
    });
    refresh();
  };

  const updateStatus = async (id: string, status: string) => {
    if (!ensureDeliveryAccess(id)) return;

    try {
      await axios.patch(`${API_BASE}/api/delivery/${id}/status`, { status });
      refresh();
    } catch (err) {
      console.error("Status update failed", err);
    }
  };

  /* ================= STATUS COLORS ================= */
  const getStatusColor = (status: string) => {
    switch (status) {
      case "requested":
        return "orange";
      case "assigned":
        return "purple";
      case "accepted":
        return "blue";
      case "in_transit":
        return "yellow";
      case "delivered":
        return "green";
      case "cancelled":
        return "red";
      case "returned":
        return "pink";
      default:
        return "gray";
    }
  };

  /* ================= FILTER / SEARCH / SORT ================= */
  const filteredDeliveries = useMemo(() => {
    let data = [...safeDeliveries];
    const q = search.trim().toLowerCase();

    if (q) {
      data = data.filter((d) => {
        const pickup = (d.pickup?.address || "").toLowerCase();
        const dropoff = (d.dropoff?.address || "").toLowerCase();
        const customer = (d.name || "").toLowerCase();
        const phone = (d.phone || "").toLowerCase();
        const status = (d.status || "").toLowerCase();
        const id = (d._id || "").toLowerCase();
        const driverName = getDriverName(d.driverId).toLowerCase();

        return (
          pickup.includes(q) ||
          dropoff.includes(q) ||
          customer.includes(q) ||
          phone.includes(q) ||
          status.includes(q) ||
          id.includes(q) ||
          driverName.includes(q)
        );
      });
    }

    if (statusFilter !== "all") {
      data = data.filter((d) => (d.status || "") === statusFilter);
    }

    data.sort((a, b) => {
      if (sortBy === "customer-asc")
        return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "customer-desc")
        return (b.name || "").localeCompare(a.name || "");
      if (sortBy === "status-asc")
        return (a.status || "").localeCompare(b.status || "");
      if (sortBy === "status-desc")
        return (b.status || "").localeCompare(a.status || "");

      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;

      if (sortBy === "oldest") return aDate - bDate;
      return bDate - aDate; // newest
    });

    return data;
  }, [safeDeliveries, search, statusFilter, sortBy, driverMap]);

  /* ================= PAGINATION ================= */
  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalItems,
    paginatedItems,
  } = usePagination<DeliveryRow>(filteredDeliveries, 10);

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));

    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [currentPage, pageSize, setCurrentPage, totalItems]);

  /* ================= GROUPING ================= */
  const groupedPaginatedDeliveries = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "All Deliveries", items: paginatedItems }];
    }

    const map = new Map<string, DeliveryRow[]>();

    for (const d of paginatedItems) {
      let key = "Other";
      if (groupBy === "status") key = d.status || "unknown";
      if (groupBy === "driver") key = getDriverName(d.driverId);

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }

    return Array.from(map.entries()).map(([key, items]) => ({ key, items }));
  }, [paginatedItems, groupBy, driverMap]);

  const baseControlStyle = {
    border: `1px solid ${theme.border}`,
    borderRadius: cardsBorderRadii,
    boxShadow: softShadow,
    bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.92)",
    color: textColor,
    _hover: {
      borderColor: brand.electricBlue,
      bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,1)",
    },
    _focus: {
      borderColor: brand.electricBlue,
      boxShadow: `0 0 0 1px ${brand.electricBlue}`,
    },
  };

  if (loading || accessLoading || superAdminLoading) {
    return (
      <Box h="100%" minH="100%" w="100%" bg={pageBg}>
        <Flex justify="center" align="center" h="100%" minH="100%">
          <GlobalSearch />
        </Flex>
      </Box>
    );
  }

  return (
    <Box h="100%" minH="100%" w="100%" bg={pageBg}>
      <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
        <Box
          flex="1"
          minH={0}
          w="100%"
          borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
          bg={shellBg}
          boxShadow={panelShadow}
          p={{ base: 2, md: 3 }}
          backdropFilter={isDark ? "blur(10px)" : undefined}
        >
          {/* Header */}
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            mb={3}
          >
            <Box>
              <Heading size="lg" color={textColor}>
                Delivery Admin
              </Heading>
              <Text color={mutedText} fontWeight="500" mt={1}>
                Search, filter, group, paginate, assign drivers and update
                delivery statuses.
              </Text>
            </Box>

            <HStack
              px={3}
              py={2}
              borderRadius={cardsBorderRadii}
              border={`1px solid ${theme.border}`}
              bg={isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)"}
              boxShadow={softShadow}
              spacing={2}
            >
              <Box
                w="8px"
                h="8px"
                borderRadius={cardsBorderRadii}
                bg={brand.electricBlue}
              />
              <Text color={textColor} fontWeight="700" fontSize="sm">
                {safeDeliveries.length} total deliveries
              </Text>
            </HStack>
          </Flex>

          {/* Empty */}
          {!safeDeliveries.length && (
            <Box
              borderRadius={cardsBorderRadii}
              bg={cardBg}
              boxShadow={softShadow}
              border={`1px solid ${theme.border}`}
              p={8}
            >
              <Text textAlign="center" color={mutedText}>
                No deliveries found.
              </Text>
            </Box>
          )}

          {!!safeDeliveries.length && (
            <>
              {/* Controls */}
              <Box
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                boxShadow={softShadow}
                border={`1px solid ${theme.border}`}
                p={{ base: 3, md: 4 }}
                mb={3}
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  bg: gradients.primaryButton || brand.electricBlue,
                }}
              >
                <Flex
                  gap={3}
                  wrap="wrap"
                  align="center"
                  direction={{ base: "column", md: "row" }}
                >
                  <Input
                    placeholder="Search route, customer, phone, driver, status, ID..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "360px" }}
                    {...baseControlStyle}
                  />

                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "190px" }}
                    {...baseControlStyle}
                    sx={{
                      option: {
                        background: isDark ? "#1f2430" : "#ffffff",
                        color: isDark ? "#f7fafc" : "#1a202c",
                      },
                    }}
                  >
                    <option value="all">All statuses</option>
                    <option value="requested">requested</option>
                    <option value="assigned">assigned</option>
                    <option value="accepted">accepted</option>
                    <option value="in_transit">in_transit</option>
                    <option value="delivered">delivered</option>
                    <option value="cancelled">cancelled</option>
                    <option value="returned">returned</option>
                  </Select>

                  <Select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "190px" }}
                    {...baseControlStyle}
                    sx={{
                      option: {
                        background: isDark ? "#1f2430" : "#ffffff",
                        color: isDark ? "#f7fafc" : "#1a202c",
                      },
                    }}
                  >
                    <option value="newest">Sort: Newest</option>
                    <option value="oldest">Sort: Oldest</option>
                    <option value="customer-asc">Customer: A → Z</option>
                    <option value="customer-desc">Customer: Z → A</option>
                    <option value="status-asc">Status: A → Z</option>
                    <option value="status-desc">Status: Z → A</option>
                  </Select>

                  <Select
                    value={groupBy}
                    onChange={(e) => {
                      setGroupBy(
                        e.target.value as "none" | "status" | "driver",
                      );
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "180px" }}
                    {...baseControlStyle}
                    sx={{
                      option: {
                        background: isDark ? "#1f2430" : "#ffffff",
                        color: isDark ? "#f7fafc" : "#1a202c",
                      },
                    }}
                  >
                    <option value="none">Group: None</option>
                    <option value="status">Group: Status</option>
                    <option value="driver">Group: Driver</option>
                  </Select>

                  <HStack
                    ml={{ md: "auto" }}
                    spacing={2}
                    px={3}
                    py={2}
                    borderRadius={cardsBorderRadii}
                    border={`1px solid ${theme.border}`}
                    bg={isDark ? "rgba(255,255,255,0.02)" : softBg}
                    boxShadow={softShadow}
                    w={{ base: "100%", md: "auto" }}
                  >
                    <Box
                      w="7px"
                      h="7px"
                      borderRadius={cardsBorderRadii}
                      bg={brand.indigo}
                    />
                    <Text color={mutedText} fontSize="sm" fontWeight="600">
                      Filtered:{" "}
                      <Text as="span" color={textColor}>
                        {filteredDeliveries.length}
                      </Text>{" "}
                      / {safeDeliveries.length}
                    </Text>
                  </HStack>
                </Flex>
              </Box>

              {/* Desktop Table */}
              <TableContainer
                bg={cardBg}
                borderRadius={cardsBorderRadii}
                boxShadow={softShadow}
                border={`1px solid ${theme.border}`}
                display={{ base: "none", md: "block" }}
                position="relative"
                overflow="hidden"
                mb={3}
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  bg: gradients.tabHikesSelected || brand.electricBlue,
                }}
              >
                <Table
                  variant="simple"
                  size={{ base: "xs", md: "sm" }}
                  colorScheme="teal"
                  minW="980px"
                >
                  <Thead
                    bg={
                      isDark
                        ? "rgba(255,255,255,0.02)"
                        : "rgba(255,255,255,0.6)"
                    }
                  >
                    <Tr>
                      <Th color={mutedText}>Route</Th>
                      <Th color={mutedText}>Customer</Th>
                      <Th color={mutedText}>Status</Th>
                      <Th color={mutedText}>Driver</Th>
                      <Th color={mutedText}>Created</Th>
                      <Th color={mutedText} textAlign="right">
                        Actions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupedPaginatedDeliveries.map((group) => (
                      <React.Fragment key={`group-${group.key}`}>
                        {groupBy !== "none" && (
                          <Tr>
                            <Td
                              colSpan={6}
                              bg={isDark ? "rgba(255,255,255,0.015)" : softBg}
                            >
                              <Flex justify="space-between" align="center">
                                <Text
                                  color={textColor}
                                  fontWeight="700"
                                  textTransform="capitalize"
                                >
                                  {group.key}
                                </Text>
                                <Badge
                                  borderRadius={cardsBorderRadii}
                                  px={2.5}
                                  py={1}
                                  bg={
                                    isDark
                                      ? "rgba(39,183,255,0.12)"
                                      : "rgba(39,183,255,0.08)"
                                  }
                                  color={isDark ? "blue.200" : "blue.700"}
                                >
                                  {group.items.length} on this page
                                </Badge>
                              </Flex>
                            </Td>
                          </Tr>
                        )}

                        {group.items.map((delivery) => (
                          <Tr
                            key={delivery._id}
                            _hover={{
                              bg: isDark
                                ? "rgba(255,255,255,0.02)"
                                : "rgba(39,183,255,0.03)",
                            }}
                            transition="all 0.2s ease"
                          >
                            <Td color={textColor}>
                              <Flex flexDirection="column">
                                <Text fontWeight="bold" noOfLines={1}>
                                  {delivery.pickup?.address || "—"}
                                </Text>
                                <ArrowDown color="grey" size={18} />
                                <Text noOfLines={1}>
                                  {delivery.dropoff?.address || "—"}
                                </Text>
                                <Text
                                  fontSize="xs"
                                  color={subtleText}
                                  mt={1}
                                  noOfLines={1}
                                >
                                  ID: {delivery._id}
                                </Text>
                              </Flex>
                            </Td>

                            <Td>
                              <Text fontWeight="600" color={textColor}>
                                {delivery.name || "—"}
                              </Text>
                              <Text fontSize="xs" color={mutedText}>
                                {delivery.phone || "—"}
                              </Text>
                            </Td>

                            <Td>
                              <Badge
                                colorScheme={getStatusColor(
                                  delivery.status || "",
                                )}
                                textTransform="capitalize"
                                px={2.5}
                                py={1}
                                borderRadius={cardsBorderRadii}
                              >
                                {delivery.status || "unknown"}
                              </Badge>
                            </Td>

                            <Td>
                              {delivery.driverId ? (
                                <Text fontWeight="medium" color={textColor}>
                                  {getDriverName(delivery.driverId)}
                                </Text>
                              ) : (
                                <Select
                                  size="xs"
                                  placeholder="Assign driver"
                                  onChange={(e) =>
                                    assignDriver(delivery._id, e.target.value)
                                  }
                                  borderRadius={cardsBorderRadii}
                                  border={`1px solid ${theme.border}`}
                                  bg={
                                    isDark
                                      ? "rgba(255,255,255,0.03)"
                                      : "rgba(255,255,255,0.95)"
                                  }
                                  color={textColor}
                                  sx={{
                                    option: {
                                      background: isDark
                                        ? "#1f2430"
                                        : "#ffffff",
                                      color: isDark ? "#f7fafc" : "#1a202c",
                                    },
                                  }}
                                >
                                  {drivers.map((d) => (
                                    <option key={d.id} value={d.id}>
                                      {d.name}
                                    </option>
                                  ))}
                                </Select>
                              )}
                            </Td>

                            <Td color={mutedText}>
                              {delivery.createdAt
                                ? new Date(delivery.createdAt).toLocaleString()
                                : "—"}
                            </Td>

                            <Td textAlign="right">
                              <HStack justify="flex-end" spacing={2}>
                                {delivery.status === "assigned" && (
                                  <Button
                                    size="xs"
                                    colorScheme="blue"
                                    onClick={() => acceptDelivery(delivery._id)}
                                  >
                                    Accept
                                  </Button>
                                )}

                                {delivery.status === "accepted" && (
                                  <Button
                                    size="xs"
                                    colorScheme="yellow"
                                    onClick={() =>
                                      updateStatus(delivery._id, "in_transit")
                                    }
                                  >
                                    Start
                                  </Button>
                                )}

                                {delivery.status === "in_transit" && (
                                  <>
                                    <Button
                                      size="xs"
                                      colorScheme="green"
                                      onClick={() =>
                                        updateStatus(delivery._id, "delivered")
                                      }
                                    >
                                      Complete
                                    </Button>

                                    <Button
                                      size="xs"
                                      colorScheme="pink"
                                      onClick={() =>
                                        updateStatus(delivery._id, "returned")
                                      }
                                    >
                                      Return
                                    </Button>
                                  </>
                                )}

                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={() =>
                                    navigate(`/delivery/${delivery._id}`)
                                  }
                                >
                                  View
                                </Button>
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {/* Mobile Cards */}
              <VStack
                spacing={4}
                align="stretch"
                display={{ base: "flex", md: "none" }}
                mb={3}
              >
                {groupedPaginatedDeliveries.map((group) => (
                  <React.Fragment key={`m-group-${group.key}`}>
                    {groupBy !== "none" && (
                      <HStack
                        justify="space-between"
                        px={3}
                        py={2}
                        borderRadius={cardsBorderRadii}
                        border={`1px solid ${theme.border}`}
                        bg={isDark ? "rgba(255,255,255,0.02)" : softBg}
                      >
                        <Text
                          color={textColor}
                          fontWeight="700"
                          textTransform="capitalize"
                        >
                          {group.key}
                        </Text>
                        <Badge colorScheme="gray">{group.items.length}</Badge>
                      </HStack>
                    )}

                    {group.items.map((delivery) => (
                      <Box
                        key={delivery._id}
                        borderWidth="1px"
                        borderColor={theme.border}
                        borderRadius={cardsBorderRadii}
                        boxShadow={softShadow}
                        p={4}
                        bg={cardBg}
                        position="relative"
                        overflow="hidden"
                        _before={{
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "3px",
                          bg: gradients.primaryButton || brand.electricBlue,
                        }}
                      >
                        <Stack spacing={3}>
                          <HStack justify="space-between" align="start">
                            <Badge
                              colorScheme={getStatusColor(
                                delivery.status || "",
                              )}
                            >
                              {delivery.status || "unknown"}
                            </Badge>
                            <Text fontSize="xs" color={subtleText}>
                              {delivery.createdAt
                                ? new Date(
                                    delivery.createdAt,
                                  ).toLocaleDateString()
                                : ""}
                            </Text>
                          </HStack>

                          <Text fontWeight="bold" color={textColor}>
                            {delivery.pickup?.address || "—"}
                          </Text>

                          <ArrowRight size={16} color="grey" />

                          <Text color={textColor}>
                            {delivery.dropoff?.address || "—"}
                          </Text>

                          <Box>
                            <Text fontWeight="600" color={textColor}>
                              {delivery.name || "—"}
                            </Text>
                            <Text fontSize="sm" color={mutedText}>
                              {delivery.phone || "—"}
                            </Text>
                          </Box>

                          <Text fontSize="sm" color={mutedText}>
                            Driver: {getDriverName(delivery.driverId)}
                          </Text>

                          {!delivery.driverId && (
                            <Select
                              size="sm"
                              placeholder="Assign driver"
                              onChange={(e) =>
                                assignDriver(delivery._id, e.target.value)
                              }
                              borderRadius={cardsBorderRadii}
                              border={`1px solid ${theme.border}`}
                              bg={
                                isDark
                                  ? "rgba(255,255,255,0.03)"
                                  : "rgba(255,255,255,0.95)"
                              }
                              color={textColor}
                              sx={{
                                option: {
                                  background: isDark ? "#1f2430" : "#ffffff",
                                  color: isDark ? "#f7fafc" : "#1a202c",
                                },
                              }}
                            >
                              {drivers.map((d) => (
                                <option key={d.id} value={d.id}>
                                  {d.name}
                                </option>
                              ))}
                            </Select>
                          )}

                          {delivery.status === "assigned" && (
                            <Button
                              size="sm"
                              colorScheme="blue"
                              onClick={() => acceptDelivery(delivery._id)}
                            >
                              Accept Delivery
                            </Button>
                          )}

                          {delivery.status === "accepted" && (
                            <Button
                              size="sm"
                              colorScheme="yellow"
                              onClick={() =>
                                updateStatus(delivery._id, "in_transit")
                              }
                            >
                              Start Delivery
                            </Button>
                          )}

                          {delivery.status === "in_transit" && (
                            <>
                              <Button
                                size="sm"
                                colorScheme="green"
                                onClick={() =>
                                  updateStatus(delivery._id, "delivered")
                                }
                              >
                                Mark Delivered
                              </Button>

                              <Button
                                size="sm"
                                colorScheme="pink"
                                onClick={() =>
                                  updateStatus(delivery._id, "returned")
                                }
                              >
                                Return
                              </Button>
                            </>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              navigate(`/delivery/${delivery._id}`)
                            }
                          >
                            View
                          </Button>
                        </Stack>
                      </Box>
                    ))}
                  </React.Fragment>
                ))}
              </VStack>

              {/* Pagination */}
              <AppPagination
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                showPageSizeSelector
                showSummary
              />
            </>
          )}
        </Box>
      </VStack>
    </Box>
  );
};
